import { Injectable } from '@angular/core';
import { FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { DynamicFieldConfig, FieldValidations, ErrorMessages, CustomValidator } from '../interfaces';
import { DEFAULT_VALIDATIONS, DEFAULT_MESSAGES, FIELD_SPECIFIC_MESSAGES } from '../constants';

@Injectable({
    providedIn: 'root',
})
export class ValidationDynamicFormService {
    constructor(private fb: FormBuilder) {}

    /**
     * Constrói validadores para um campo
     */
    buildValidators(field: DynamicFieldConfig): any[] {
        const validators: any[] = [];

        // Adicionar validação required se necessário
        if (field.required) {
            validators.push(Validators.required);
        }

        // Adicionar validações customizadas do campo
        if (field.validations) {
            this.addCustomValidations(validators, field.validations);
        }

        // Adicionar validações padrão do tipo
        this.addDefaultValidations(validators, field);

        return validators;
    }

    /**
     * Adiciona validações customizadas
     */
    private addCustomValidations(validators: any[], validations: FieldValidations): void {
        if (validations.minLength) {
            validators.push(Validators.minLength(validations.minLength));
        }
        if (validations.maxLength) {
            validators.push(Validators.maxLength(validations.maxLength));
        }
        if (validations.min) {
            validators.push(Validators.min(validations.min));
        }
        if (validations.max) {
            validators.push(Validators.max(validations.max));
        }
        if (validations.pattern) {
            validators.push(Validators.pattern(validations.pattern));
        }
        if (validations.email) {
            validators.push(Validators.email);
        }
        if (validations.password) {
            validators.push(this.passwordValidator());
        }
        if (validations.custom) {
            validators.push(validations.custom);
        }
    }

    /**
     * Adiciona validações padrão baseadas no tipo
     * Não aplica validações padrão se já existir uma validação customizada do mesmo tipo
     */
    private addDefaultValidations(validators: any[], field: DynamicFieldConfig): void {
        const defaultValidations = DEFAULT_VALIDATIONS[field.type];
        if (defaultValidations) {
            const customValidations = field.validations || {};

            Object.keys(defaultValidations).forEach(key => {
                // Pular validação padrão se já existe uma customizada do mesmo tipo
                if (customValidations[key as keyof typeof customValidations] !== undefined) {
                    return;
                }

                const value = defaultValidations[key];
                switch (key) {
                    case 'minLength':
                        validators.push(Validators.minLength(value));
                        break;
                    case 'maxLength':
                        validators.push(Validators.maxLength(value));
                        break;
                    case 'min':
                        validators.push(Validators.min(value));
                        break;
                    case 'max':
                        validators.push(Validators.max(value));
                        break;
                    case 'pattern':
                        validators.push(Validators.pattern(value));
                        break;
                    case 'email':
                        validators.push(Validators.email);
                        break;
                    case 'password':
                        validators.push(this.passwordValidator());
                        break;
                }
            });
        }
    }

    /**
     * Validador customizado para senha forte
     * Alinhado com as regras do backend
     */
    passwordValidator(): CustomValidator {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;

            // Mínimo 8 caracteres
            if (value.length < 8) {
                return { password: { minLength: true } };
            }

            // Pelo menos uma letra maiúscula
            if (!/[A-Z]/.test(value)) {
                return { password: { uppercase: true } };
            }

            // Pelo menos uma letra minúscula
            if (!/[a-z]/.test(value)) {
                return { password: { lowercase: true } };
            }

            // Pelo menos um número
            if (!/\d/.test(value)) {
                return { password: { number: true } };
            }

            // Pelo menos um caractere especial (mesmo padrão do backend)
            if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
                return { password: { special: true } };
            }

            return null;
        };
    }

    /**
     * Obtém mensagem de erro para um campo
     */
    getErrorMessage(field: DynamicFieldConfig, control: AbstractControl): string {
        if (!control.errors) return '';

        const errorKey = Object.keys(control.errors)[0];
        const allMessages = this.getAllMessages(field);

        // Verificar mensagem customizada primeiro
        if (field.errorMessages && field.errorMessages[errorKey]) {
            return this.interpolateMessage(field.errorMessages[errorKey]!, control.errors[errorKey]);
        }

        // Verificar mensagem específica do tipo
        const fieldSpecificMessages = FIELD_SPECIFIC_MESSAGES[field.type];
        if (fieldSpecificMessages && fieldSpecificMessages[errorKey]) {
            return this.interpolateMessage(fieldSpecificMessages[errorKey], control.errors[errorKey]);
        }

        // Usar mensagem padrão
        const defaultMessage = allMessages[errorKey];
        if (defaultMessage) {
            return this.interpolateMessage(defaultMessage, control.errors[errorKey]);
        }

        return field.errorMessages?.custom || 'Campo inválido';
    }

    /**
     * Combina todas as mensagens disponíveis
     */
    private getAllMessages(field: DynamicFieldConfig): ErrorMessages {
        const allMessages = { ...DEFAULT_MESSAGES };

        // Adicionar mensagens específicas do tipo
        const fieldSpecificMessages = FIELD_SPECIFIC_MESSAGES[field.type];
        if (fieldSpecificMessages) {
            Object.assign(allMessages, fieldSpecificMessages);
        }

        // Adicionar mensagens customizadas do campo
        if (field.errorMessages) {
            Object.assign(allMessages, field.errorMessages);
        }

        return allMessages;
    }

    /**
     * Interpola valores na mensagem
     */
    private interpolateMessage(message: string, error: any): string {
        if (typeof error === 'object') {
            Object.keys(error).forEach(key => {
                message = message.replace(`{${key}}`, error[key]);
            });
        }
        return message;
    }
}
